/**
 * Roadmap Page
 * @implements FS-005.6.1 - View Roadmap
 * @implements FS-005.6.3 - View Milestone Details
 * @implements FS-005.6.4 - View Epic Details
 * @implements US-037 - Basic Roadmap Viewing
 *
 * Displays the roadmap for a project as a kanban board.
 * Supports detail drawers for both milestones and epics via URL query params.
 * Route: /projects/:projectId/roadmap
 */

import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Container,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
import { KanbanBoardContainer } from '@/components/roadmap';

/**
 * GraphQL query for fetching project title
 */
const GET_PROJECT_QUERY = gql`
  query GetProjectForRoadmap($id: ID!) {
    project(id: $id) {
      id
      title
      lifecycleState
    }
  }
`;

/**
 * Project type from the GraphQL response
 */
interface Project {
  id: string;
  title: string;
  lifecycleState: string;
}

/**
 * Response type for GetProject query
 */
interface GetProjectResponse {
  project: Project | null;
}

/**
 * Variables for GetProject query
 */
interface GetProjectVariables {
  id: string;
}

/**
 * RoadmapPage - Page component for viewing the project roadmap
 *
 * @implements FS-005.6.1 - View Roadmap
 * @implements US-037 - Basic Roadmap Viewing
 *
 * Features:
 * - Fetches project data for breadcrumbs
 * - Displays KanbanBoardContainer component
 * - Handles loading, error, and 404 states
 * - Provides navigation back to project
 *
 * Route: /projects/:projectId/roadmap
 */
export const RoadmapPage = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  // Fetch project data for breadcrumbs
  const { data, loading, error } = useQuery<
    GetProjectResponse,
    GetProjectVariables
  >(GET_PROJECT_QUERY, {
    variables: { id: projectId! },
    skip: !projectId,
    fetchPolicy: 'cache-and-network',
  });

  const project = data?.project;

  // Render loading state
  if (loading && !data) {
    return (
      <Container maxWidth="md">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Render error state
  if (error) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load project: {error.message}
          </Alert>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/projects')}
          >
            Back to Projects
          </Button>
        </Box>
      </Container>
    );
  }

  // Render 404 state (project not found)
  if (!project) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: 'center',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h5" gutterBottom>
              Project Not Found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              The project you are looking for does not exist or you do not have
              access to it.
            </Typography>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/projects')}
            >
              Back to Projects
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        {/* @implements US-037 - Breadcrumb navigation: Projects > {Project Name} > Roadmap */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 3 }}
        >
          <Link
            component={RouterLink}
            to="/projects"
            color="inherit"
            underline="hover"
          >
            Projects
          </Link>
          <Link
            component={RouterLink}
            to={`/projects/${projectId}`}
            color="inherit"
            underline="hover"
          >
            {project.title}
          </Link>
          <Typography color="text.primary">Roadmap</Typography>
        </Breadcrumbs>

        {/* Page Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Roadmap
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Milestones and epics for {project.title}
          </Typography>
        </Box>

        {/* Kanban Board */}
        <Box
          sx={{
            flex: 1,
            minHeight: 'calc(100vh - 280px)',
            bgcolor: 'grey.50',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <KanbanBoardContainer projectId={projectId!} />
        </Box>

        {/* Back to Project Link */}
        <Box sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            component={RouterLink}
            to={`/projects/${projectId}`}
          >
            Back to Project
          </Button>
        </Box>
      </Box>
    </Container>
  );
};
